import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const location = window.location;
  const searchParams = new URLSearchParams(location.search);
  const redirect = searchParams.get("redirect");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      if (redirect) {
        navigate(redirect);
      } else {
        navigate("/app");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (!err.response) {
        toast.error("Impossible de joindre le serveur. Vérifiez votre connexion internet.");
      } else if (
        err.response.status === 401 ||
        detail === "Incorrect username or password" ||
        detail === "Identifiants invalides"
      ) {
        toast.error("Email ou mot de passe incorrect.");
      } else if (typeof detail === "string") {
        toast.error(detail);
      } else {
        toast.error("Une erreur est survenue lors de la connexion.");
      }
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-parchment">
      {/* Left Side: Elegant Visual */}
      <div className="hidden lg:block relative overflow-hidden">
        <img 
          src="https://static.prod-images.emergentagent.com/jobs/c7c691e5-c2e1-48ee-9432-1c4ee8093bb8/images/fada99ebaf84b6a35cc6212d78360cceaa8252ea57df673f38cbcc00e7b8c811.png" 
          alt="Abstract Ceremony" 
          className="w-full h-full object-cover scale-100 hover:scale-102 transition-transform duration-1000" 
        />
        <div className="absolute inset-0 bg-charcoal/10" />
        <div className="absolute bottom-16 left-16 text-white space-y-2">
          <div className="overline text-white/80 tracking-[0.3em] font-semibold text-xs">Ceremind</div>
          <div className="font-serif text-5xl font-light tracking-tight leading-tight">Bon retour.</div>
          <div className="text-sm font-light text-white/70 max-w-sm">Accédez à votre espace de création et d'organisation personnalisé.</div>
        </div>
      </div>

      {/* Right Side: Form */}
      <div className="flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-md bg-white border border-border-cream p-8 lg:p-12 rounded-sm shadow-sm">
          <form onSubmit={submit} className="space-y-6" data-testid="login-form">
            <div>
              <Link to="/" className="text-xs uppercase tracking-widest font-semibold text-sage hover:text-sage/80 transition-colors flex items-center gap-1">
                ← Retour à l'accueil
              </Link>
              <h1 className="font-serif text-4xl mt-6 text-charcoal font-light">Connexion</h1>
              <p className="text-xs text-charcoal-soft mt-1 font-light">Saisissez vos identifiants pour gérer votre mariage.</p>
            </div>

            <div className="space-y-4">
              <label className="block">
                <span className="overline text-[10px] text-charcoal-soft font-semibold">Adresse Email</span>
                <input 
                  data-testid="login-email" 
                  name="email"
                  required 
                  type="email" 
                  autoComplete="email"
                  value={email} 
                  onChange={(e) => setEmail(e.target.value)} 
                  className="mt-2 w-full px-4 py-3 bg-parchment border border-border-cream rounded-sm text-sm text-charcoal focus:border-sage focus:outline-none transition-colors" 
                />
              </label>

              <label className="block">
                <span className="overline text-[10px] text-charcoal-soft font-semibold">Mot de passe</span>
                <input 
                  data-testid="login-password" 
                  name="password"
                  required 
                  type="password" 
                  autoComplete="current-password"
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)} 
                  className="mt-2 w-full px-4 py-3 bg-parchment border border-border-cream rounded-sm text-sm text-charcoal focus:border-sage focus:outline-none transition-colors" 
                />
              </label>
              <div className="flex justify-end">
                <Link to="/forgot-password" className="text-[10px] uppercase tracking-widest text-charcoal hover:text-sage transition-colors">
                  Mot de passe oublié ?
                </Link>
              </div>
            </div>

            <button 
              data-testid="login-submit" 
              disabled={loading} 
              className="btn-sage w-full py-3.5 text-xs font-bold uppercase tracking-widest rounded-sm disabled:opacity-50"
            >
              {loading ? "Connexion en cours…" : "Se connecter"}
            </button>

            <div className="text-xs text-charcoal-soft text-center pt-2 font-light">
              Pas encore de compte ?{" "}
              <Link to="/register" className="underline font-normal text-charcoal hover:text-sage transition-colors">
                Créer un espace
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
